import { Fragment, useRef, useState } from 'react'
import {
  PiArrowBendUpLeft,
  PiPaperclip,
  PiPaperPlaneRight,
} from 'react-icons/pi'
import { Button, FilterChip, RichTextEditor } from '@firstbite/ui'
import { toast } from 'react-toastify'

export type EmailAttachment = { name: string; url?: string }

// Matches http(s):// URLs, www.* URLs, and bare domain+path links (e.g.
// `meet.google.com/abc`) so a flattened email snippet's links stay clickable.
const URL_RE =
  /((?:https?:\/\/|www\.)[^\s]+|(?:[a-z0-9-]+\.)+[a-z]{2,}\/[^\s]+)/gi

/** Render plain snippet text with its URLs as real (new-tab) links. */
function linkify(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  const re = new RegExp(URL_RE)
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    let url = m[0]
    // Don't swallow trailing sentence punctuation into the link.
    const trail = url.match(/[.,;:!?)\]}]+$/)
    const trailing = trail ? trail[0] : ''
    if (trailing) url = url.slice(0, -trailing.length)
    if (m.index > last) out.push(text.slice(last, m.index))
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`
    out.push(
      <a
        key={m.index}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="break-all text-brand-active transition-colors hover:text-basil-800"
      >
        {url}
      </a>
    )
    if (trailing) out.push(trailing)
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out.map((node, i) => <Fragment key={i}>{node}</Fragment>)
}

/** A download/label chip for an email attachment (link when a URL is present). */
function EmailAttachmentChip({ att }: { att: EmailAttachment }) {
  const inner = (
    <>
      <PiPaperclip size={13} />
      <span className="max-w-[12rem] truncate">{att.name}</span>
    </>
  )
  const chipClass =
    'inline-flex items-center gap-1.5 rounded-md border border-line-subtle bg-surface px-2 py-1 text-xs text-fg-secondary'
  return att.url ? (
    <a
      href={att.url}
      target="_blank"
      rel="noreferrer"
      className={`${chipClass} transition-colors hover:text-brand-active`}
    >
      {inner}
    </a>
  ) : (
    <span className={chipClass}>{inner}</span>
  )
}

/**
 * Expanded content for an email activity item — the message + any attachments,
 * with an inline reply editor that can itself attach files. Shared by the
 * contact and deal detail-page timelines so both render emails identically.
 * Sending is currently a stub (toast) — no email backend is wired here yet.
 */
export function TimelineEmail({
  from,
  subject,
  body,
  attachments = [],
  onViewThread,
}: {
  from: string
  subject: string
  body: string
  attachments?: EmailAttachment[]
  /** When set, shows a "View thread" action that opens the full email thread
   *  (e.g. in the inbox modal). Omit for surfaces with no real thread. */
  onViewThread?: () => void
}) {
  const [replying, setReplying] = useState(false)
  const [replyFiles, setReplyFiles] = useState<File[]>([])
  const replyFileRef = useRef<HTMLInputElement>(null)

  const closeReply = () => {
    setReplying(false)
    setReplyFiles([])
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-fg-secondary">
        From: {from} · Subject: {subject}
      </p>
      <p className="whitespace-pre-wrap break-words text-sm text-fg">
        {linkify(body)}
      </p>
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((att, i) => (
            <EmailAttachmentChip key={`${att.name}-${i}`} att={att} />
          ))}
        </div>
      ) : null}
      {replying ? (
        <div className="space-y-2 rounded-lg border border-line-subtle p-2">
          <RichTextEditor
            contentLabel="Reply"
            placeholder="Write your reply…"
            contentClassName="min-h-16"
            // The toolbar paperclip is the attach affordance — no separate button.
            onCommand={(cmd) => {
              if (cmd === 'attachment') replyFileRef.current?.click()
            }}
          />
          {replyFiles.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {replyFiles.map((file, i) => (
                <FilterChip
                  key={`${file.name}-${i}`}
                  value={
                    <span className="inline-flex items-center gap-1">
                      <PiPaperclip size={12} />
                      <span className="max-w-[10rem] truncate">
                        {file.name}
                      </span>
                    </span>
                  }
                  removeLabel={`Remove ${file.name}`}
                  onRemove={() =>
                    setReplyFiles((prev) => prev.filter((_, idx) => idx !== i))
                  }
                />
              ))}
            </div>
          ) : null}
          <input
            ref={replyFileRef}
            type="file"
            multiple
            hidden
            aria-label="Attach files to reply"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length) setReplyFiles((prev) => [...prev, ...files])
              e.target.value = ''
            }}
          />
          <div className="flex justify-end gap-2">
            <Button appearance="outline" size="sm" onClick={closeReply}>
              Cancel
            </Button>
            <Button
              size="sm"
              leftIcon={<PiPaperPlaneRight aria-hidden="true" />}
              onClick={() => {
                closeReply()
                toast.success('Reply sent')
              }}
            >
              Send reply
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          {onViewThread ? (
            <Button variant="ghost" size="sm" onClick={onViewThread}>
              View thread
            </Button>
          ) : null}
          <Button
            appearance="outline"
            size="sm"
            leftIcon={<PiArrowBendUpLeft aria-hidden="true" />}
            onClick={() => setReplying(true)}
          >
            Reply
          </Button>
        </div>
      )}
    </div>
  )
}
